import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { ZodIssue } from 'zod';

import type {
  ConflictResolution,
  MergeResult,
} from '@polyai/stream/services/conflict-resolution';
import type { RootState } from '@polyai/stream/store';

type NestedPaths<T, Prev extends string = ''> = {
  [K in keyof T & string]: T[K] extends Record<string, any>
    ? `${Prev}${K}` | NestedPaths<T[K], `${Prev}${K}.`>
    : `${Prev}${K}`;
}[keyof T & string];

export type RootStatePath = NestedPaths<RootState>;

export interface ConflictResolutionState {
  mergeResult?: MergeResult<RootState>;
  conflictResolutions?: ConflictResolution[];
  pendingConflictResolutions?: ConflictResolution[];
  currentEditId?: string;
  conflictPicks?: {
    [key in RootStatePath]?: ConflictResolution['strategy'];
  };
  errors?: (ZodIssue & { isResolved: boolean })[];
}

const initialState: ConflictResolutionState = {};

const conflictResolutionSlice = createSlice({
  name: 'conflictResolution',
  initialState,
  reducers: {
    setMergeResult: (state, action: PayloadAction<MergeResult<RootState>>) => {
      state.mergeResult = action.payload;
      state.errors =
        action.payload.errors?.map(error => ({
          ...error,
          isResolved: false,
        })) ?? [];
    },
    startConflictResolution: state => {
      state.conflictResolutions = [];
      state.pendingConflictResolutions = [];
    },
    upsertConflictResolution: (
      state,
      action: PayloadAction<
        Omit<ConflictResolution, 'path'> & { path: RootStatePath }
      >,
    ) => {
      state.pendingConflictResolutions = [
        ...(state.pendingConflictResolutions?.filter(
          resolution => resolution.path.join('.') !== action.payload.path,
        ) ?? []),
        { ...action.payload, path: action.payload.path.split('.') },
      ];
    },
    clearConflictState: () => {
      return initialState;
    },
    updateErrors: (
      state,
      action: PayloadAction<MergeResult<RootState>['errors']>,
    ) => {
      const newErrorsMap =
        action.payload?.reduce(
          (acc, error) => {
            const existingErrors = acc[error.path.join('.')] ?? [];
            acc[error.path.join('.')] = [...existingErrors, error.message];
            return acc;
          },
          {} as { [path: string]: string[] },
        ) ?? {};

      state.errors = [
        ...(state.errors?.map(error => {
          const path = error.path.join('.');
          const newErrorMessages = newErrorsMap[path] ?? [];

          if (newErrorMessages.includes(error.message)) {
            return undefined;
          }

          return { ...error, isResolved: true };
        }) ?? []),
        ...(action.payload?.map(error => ({ ...error, isResolved: false })) ??
          []),
      ].filter(e => !!e);
    },
    saveEdits: state => {
      const pendingConflictPaths = new Set(
        (state.pendingConflictResolutions ?? []).map(resolution =>
          resolution.path.join('.'),
        ),
      );
      state.conflictResolutions = [
        ...(state.conflictResolutions ?? []).filter(
          resolution => !pendingConflictPaths.has(resolution.path.join('.')),
        ),
        ...(state.pendingConflictResolutions ?? []),
      ];
      state.pendingConflictResolutions = [];
      state.currentEditId = undefined;
    },
    clearEdits: state => {
      state.pendingConflictResolutions = [];
      state.currentEditId = undefined;
    },
    setCurrentEditId: (state, action: PayloadAction<string>) => {
      state.currentEditId = action.payload;
    },
    pickResolution: (
      state,
      action: PayloadAction<{
        path: RootStatePath;
        strategy: ConflictResolution['strategy'];
      }>,
    ) => {
      state.conflictPicks = {
        ...state.conflictPicks,
        [action.payload.path]: action.payload.strategy,
      };
    },
  },
});

export const {
  setMergeResult,
  startConflictResolution,
  upsertConflictResolution,
  clearConflictState,
  saveEdits,
  clearEdits,
  setCurrentEditId,
  pickResolution,
  updateErrors,
} = conflictResolutionSlice.actions;

export default conflictResolutionSlice.reducer;
